/**
 * Fetches the price preview for a seat upgrade.
 *
 * The preview endpoint wants the new seat total rather than the number of added seats, so we
 * send `currentSeatTotal + newlyPurchased`. What comes back is Chargebee's immediate invoice
 * for the change: the prorated charge for the added seats, not the price of the full total.
 */
export default function useProratedSeatPrice(
  businessId: MaybeRefOrGetter<string>,
  currentSeatTotal: MaybeRefOrGetter<number>,
  newlyPurchased: MaybeRefOrGetter<number>
) {
  const { $api } = useNuxtApp()
  const { logError } = useErrorHandling()

  const payNowAmount = ref<number | null>(null)
  const isLoading = ref(false)

  const fetchProratedPrice = async () => {
    if (toValue(newlyPurchased) <= 0) {
      payNowAmount.value = null
      return
    }
    isLoading.value = true
    try {
      const newTotal = toValue(currentSeatTotal) + toValue(newlyPurchased)
      const { amountSubtotal, amountDiscountTotal } = await $api(
        `/api/businesses/${toValue(businessId)}/subscriptions/seats/preview`,
        { query: { seats: newTotal } }
      )
      payNowAmount.value = amountSubtotal - amountDiscountTotal
    } catch (error) {
      logError('Failed to fetch prorated seat price', error)
      payNowAmount.value = null
    } finally {
      isLoading.value = false
    }
  }

  watch([() => toValue(currentSeatTotal), () => toValue(newlyPurchased)], fetchProratedPrice, { immediate: true })

  return { payNowAmount, isLoading }
}
